import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";

import { showToast } from "../../../../store/actions";
import { useCronReducer } from "../../../../util/cron";
import WorkspaceService from "../../../../service/workspace";
import { ButtonUI, ConfirmPopup } from "../../../Common";
import { FormTile } from "../../../Common/Tile";
import { setPrevState } from "../../../../util/common";
import { regexName } from "../../../../util/regularExpress";
import CommonLayout from "../../../Common/Layout";
import HelpContextIcon from "../../../Common/HelpPanel/HelpContextIcon";
import { DETAUL_CRON_VALUE, JOB_BY_TYPE } from "../utils/JobData";
import { navScheduledJobs } from "../../../kmk/AppData";
import NextJobForm from "../utils/NextJobForm";
import Scheduler from "../utils/Scheduler";
import SageMakerService from "../../../../service/sageMakerService";
import DataPipelineService from "../../../../service/dataPipelineService";
import SmallToolbar from "../../../Common/SmallToolbar";
import CloneJob from "../CloneJob";

const useStyles = makeStyles()((theme) => ({
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  BigTileContainer: {
    paddingTop: "0px !important",
    marginTop: "10px !important",
  },
  subContainer: {
    display: "flex",
    padding: 0,
    borderTop: 0,
    width: "100%",
    flexDirection: "column",
  },
  PanelContainer: {
    padding: "22px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "250px",
    marginLeft: "inherit",
    "& .MuiPaper-root": {
      height: "auto",
    },
  },
  MenuItem: {
    whiteSpace: "normal",
    wordWrap: "break-word",
    wordBreak: "break-all",
  },
}));

const TYPES = [
  {
    text: "SageMaker Pipeline",
    value: "sagemaker_pipeline",
  },
];

export default function Notebook() {
  const { Workspace_id, Job_id } = useParams();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const workspace = useSelector((state) => state.workspace);
  const { Name: workspaceName, IsDataVaultEnabled: isDataVaultEnabled, accessRole } = workspace.workspace;

  // For Schedule Panel
  const [values, dispatchValues] = useCronReducer("");

  const [jobState, setJobState] = useState({
    loading: false,
    jobName: "",
    type: TYPES[0].value,
    jobPipeline: "",
    DataPipeline_id: "",
    timeZone: "",
    nextRunTime: "",
    scheduleEnabled: "",
    isEntryJob: false,
  });

  const [dataPipelineList, setDataPipelineList] = useState([]);
  const [jobPipelineList, setJobPipelineList] = useState([]);

  const [deleteJobState, setDeleteJobState] = useState({
    openConfirm: false,
    confirmMessage: "",
    loadingForConfirm: false,
  });

  const [nextJobState, setNextJobState] = useState({
    nextJobToggle: false,
    nextDataPipelineId: "",
    nextJobName: "",
    nextJobId: "",
    activeSchedule: "",
    errorDataPipelineId: "",
    errorJobId: "",
    errorSchedule: "",
  });

  const [openClone, setOpenClone] = useState(false);

  const nextJobIdRef = useRef(nextJobState.nextJobId);
  const errorJobIdRef = useRef(nextJobState.errorJobId);
  const textRef = useRef(null);

  const disableSave = useMemo(
    () =>
      jobState.loading ||
      jobState.jobName === "" ||
      !jobState.jobPipeline ||
      !jobState.type ||
      (nextJobState.nextJobToggle === true && !(nextJobState.nextJobId || nextJobState.errorJobId)),
    [
      jobState.jobName,
      jobState.jobPipeline,
      jobState.loading,
      jobState.type,
      nextJobState.errorJobId,
      nextJobState.nextJobId,
      nextJobState.nextJobToggle,
    ]
  );

  const handleCancel = (setState, stateVar) => {
    setPrevState(setState, { [stateVar]: false });
  };

  const deleteNotebookJob = async () => {
    setPrevState(setDeleteJobState, { loadingForConfirm: true });

    const wsService = new WorkspaceService();
    const result = await wsService.deleteNotebookJob(Workspace_id, Job_id);

    if (result.success) {
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }
    setPrevState(setDeleteJobState, { openConfirm: false, loadingForConfirm: false });
  };

  const handleSave = async (e, clone = false) => {
    setPrevState(setJobState, { loading: true });

    let response = null;
    const submitData = {
      name: jobState.jobName.trim(),
      DataPipeline_id: jobState.DataPipeline_id,
      jobPipeline: jobState.jobPipeline,
      type: jobState.type,
      schedule: values.cronValue || DETAUL_CRON_VALUE,
      scheduleEnabled: jobState.scheduleEnabled || false,
      enableNextJob: nextJobState.nextJobToggle,
      ...(nextJobState.nextJobToggle
        ? {
            nextPipeline: nextJobState.nextDataPipelineId,
            nextJob: nextJobState.nextJobId,
            errorPipeline: nextJobState.errorDataPipelineId,
            errorJob: nextJobState.errorJobId,
            disableNextJob: nextJobState.activeSchedule,
            disableErrorJob: nextJobState.errorSchedule,
          }
        : {
            nextPipeline: null,
            nextJob: null,
            errorPipeline: null,
            errorJob: null,
            disableNextJob: null,
            disableErrorJob: null,
          }),
      isEntryJob: jobState.isEntryJob,
    };

    const wsService = new WorkspaceService();
    if (!Job_id) response = await wsService.createNotebookJob(submitData, Workspace_id);
    else response = await wsService.updateNotebookJob(submitData, Workspace_id, Job_id);

    if (response.success) {
      if (clone) {
        setPrevState(setJobState, { loading: false });
        return true;
      }

      dispatch(showToast({ message: response.message, type: "success" }));
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }

    setPrevState(setJobState, { loading: false });
  };

  const handleCancelClick = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
  }, [Workspace_id, navigate]);

  useEffect(() => {
    if (![1, 2].includes(accessRole)) {
      dispatch(
        showToast({
          message: "You do not have permission to perform this action!",
          type: "error",
        })
      );
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }
  }, [Workspace_id, accessRole, dispatch, navigate]);

  useEffect(() => {
    const getDataPipelineList = async () => {
      const pipeline = new DataPipelineService();
      const response = await pipeline.getDataPipelineList(Workspace_id);
      if (response.success && response.data?.dataPipelineList?.length) return response.data.dataPipelineList;
      return [];
    };

    async function fetchData() {
      setPrevState(setJobState, { loading: true });
      let initialState = {};

      const [dataPipelines, pipelinesResponse] = await Promise.all([
        getDataPipelineList(),
        new SageMakerService().getPipelines(),
      ]);

      if (pipelinesResponse.success) setJobPipelineList(pipelinesResponse.data);

      if (Job_id) {
        const getNotebookSchedule = async () => {
          const wsService = new WorkspaceService();
          const response = await wsService.getNotebookSchedule(Workspace_id, Job_id);
          if (response === "Unauthorized") return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

          if (response.success) {
            dispatchValues({
              type: "set_values",
              value: response.data.Schedule,
            });

            setPrevState(setNextJobState, {
              nextDataPipelineId: response.data.NextPipeline_id || "",
              nextJobId: response.data.NextJob_id || nextJobIdRef.current,
              errorDataPipelineId: response.data.ErrorPipeline_id || "",
              errorJobId: response.data.ErrorJob_id || errorJobIdRef.current,
              nextJobToggle: response.data.enableNextJob || false,
            });

            const jobPipeline =
              pipelinesResponse.data &&
              response.data.Detail.jobPipeline &&
              pipelinesResponse.data.some(({ value }) => value === response.data.Detail.jobPipeline)
                ? response.data.Detail.jobPipeline
                : "";

            return {
              jobName: response.data.Name,
              jobPipeline,
              type: response.data.Detail.type || "",
              DataPipeline_id: response.data.DataPipeline_id,
              timeZone: response.data.ServerTimeZone,
              scheduleEnabled: response.data.Enabled,
              nextRunTime: response.data.nextRun,
            };
          } else return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
        };

        initialState = await getNotebookSchedule();
      } else {
        const getTimezone = async () => {
          const wsService = new WorkspaceService();
          const result = await wsService.getTimezone(Workspace_id);
          return result.data;
        };

        initialState.timeZone = await getTimezone();
      }

      if (!initialState.DataPipeline_id)
        initialState.DataPipeline_id = dataPipelines?.find((pipeline) => pipeline.IsDefault === 1)?.DataPipeline_id;
      setDataPipelineList(dataPipelines);

      let isEntryJob = false;
      if (Job_id) {
        const currentPipeline = dataPipelines.find(
          ({ DataPipeline_id }) => DataPipeline_id === parseInt(initialState.DataPipeline_id)
        );

        isEntryJob = currentPipeline.EntryJob_id === parseInt(Job_id);
      }

      setPrevState(setJobState, {
        ...initialState,
        loading: false,
        isEntryJob,
      });
    }
    fetchData();
  }, [Job_id, Workspace_id, dispatchValues, navigate]);

  /**
   * Alpha Numeric Characters only with Space allow (first letter should Alpha)
   * @param {*} str
   * @returns
   */
  const handleJobName = (str, dataState) => {
    const isValid = regexName(str, "jobName", "_ -");
    if (isValid) setPrevState(setJobState, { [dataState]: str });
  };

  const resetNextJob = () =>
    setPrevState(setNextJobState, {
      activeSchedule: "",
      nextJobToggle: false,
      nextJobName: "",
      nextJobId: "",
      errorJobId: "",
      errorSchedule: "",
    });

  const confirmPipelineChange = useCallback((e) => {
    setJobState((prev) => ({
      ...prev,
      DataPipeline_id: e.target.value,
      ...(prev.DataPipeline_id ? { isEntryJob: false } : {}),
    }));
    resetNextJob();
  }, []);

  const addJobForm = () => {
    return (
      <Grid container>
        <Grid container direction={"row"} spacing={2} sx={{ mb: "16px" }}>
          <Grid>
            <TextField
              value={jobState.jobName}
              sx={{ width: { xs: "100%", sm: "300px" } }}
              fullWidth
              required
              label="Job Name"
              inputProps={{ maxLength: 30 }}
              onChange={(e) => handleJobName(e.target.value, "jobName")}
            />
          </Grid>
          <Grid>
            {!!isDataVaultEnabled && (
              <FormControl sx={{ width: { xs: "100%", sm: "300px" } }} required>
                <InputLabel>Pipeline</InputLabel>
                <Select
                  value={jobState.DataPipeline_id}
                  onChange={confirmPipelineChange}
                  label="Pipeline"
                  disabled={!!Job_id}
                  MenuProps={{ PaperProps: { style: { maxHeight: "300px", maxWidth: "300px" } } }}
                  renderValue={(selected) => {
                    const selectedPipeline = dataPipelineList.find((p) => p.DataPipeline_id === selected);
                    const isOverflow =
                      (textRef.current?.scrollWidth > textRef.current?.clientWidth || false) && !!Job_id;

                    return (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Tooltip
                          placement="bottom"
                          title={selectedPipeline?.Name}
                          arrow
                          disableHoverListener={!isOverflow}
                        >
                          <Typography
                            variant="body1"
                            ref={textRef}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "200px",
                            }}
                          >
                            {selectedPipeline?.Name || "Select a pipeline"}
                          </Typography>
                        </Tooltip>
                        {selectedPipeline?.IsDefault === 1 && (
                          <Tooltip placement="right" title="Default Pipeline" arrow>
                            <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                          </Tooltip>
                        )}
                      </Stack>
                    );
                  }}
                >
                  {dataPipelineList.map((p) => (
                    <MenuItem
                      key={p.DataPipeline_id}
                      value={p.DataPipeline_id}
                      style={{ whiteSpace: "normal", wordWrap: "break-word", wordBreak: "break-all" }}
                    >
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="body1" sx={{ m: 0, p: 0 }}>
                          {p.Name}
                        </Typography>
                        {p.IsDefault === 1 && (
                          <Tooltip placement="right" title="Default Pipeline" arrow>
                            <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                          </Tooltip>
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid sx={{ display: "flex", alignItems: "center" }}>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox />}
                value={jobState.isEntryJob}
                checked={jobState.isEntryJob}
                onChange={() => setJobState((prev) => ({ ...prev, isEntryJob: !prev.isEntryJob }))}
                label="Set as the entry point for the pipeline"
              />
            </FormGroup>
          </Grid>
        </Grid>
        <Grid container direction={"row"} spacing={2} sx={{ mb: "16px" }}>
          <Grid>
            <TextField
              select
              required
              fullWidth
              variant="outlined"
              label="Type"
              value={jobState.type}
              onChange={(e) => setJobState((prev) => ({ ...prev, type: e.target.value }))}
              sx={{ width: { xs: "100%", sm: "300px" } }}
            >
              {TYPES.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.text}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Grid
          sx={{ paddingTop: "2px !important" }}
          size={{
            sm: 12,
            md: 8,
          }}
        >
          <FormControl sx={{ width: "100%" }} variant="outlined" required>
            <InputLabel>Target</InputLabel>
            <Select
              label="Target"
              required
              fullWidth
              variant="outlined"
              value={jobState.jobPipeline}
              onChange={(e) => setJobState((prev) => ({ ...prev, jobPipeline: e.target.value }))}
              MenuProps={{ sx: { maxHeight: "400px", maxWidth: "700px" } }}
            >
              {jobPipelineList.map((p) => (
                <MenuItem key={p.value} value={p.value} className={classes.MenuItem}>
                  {p.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    {
      link: `/my-workspaces/${Workspace_id}`,
      displayName: workspaceName,
      active: false,
    },
    {
      link: `/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`,
      displayName: "Data Pipelines",
      active: false,
    },
    { link: "#", displayName: `${Job_id ? "Edit Notebook Job" : "Add Notebook Job"}`, active: true },
  ];

  const toggleSchedule = useCallback(
    () => setJobState((prev) => ({ ...prev, scheduleEnabled: !prev.scheduleEnabled })),
    []
  );

  return (
    <CommonLayout title="Job Scheduler" loading={jobState.loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          overflowX: "hidden",
        }}
        id="main_box"
      >
        <Backdrop
          sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={jobState.loading}
        >
          <CircularProgress color="primary" />
        </Backdrop>
        <Box maxWidth="lg" sx={{ mb: 4, mx: 0 }}>
          <Box className={classes.subContainer}>
            <Box className={classes.PanelContainer}>
              <Grid container direction="row" sx={{ mb: 4 }}>
                <Grid sx={{ mb: 1, display: "flex", alignItems: "center" }} size={12}>
                  <Typography className={classes.HeaderTitle} component="div">
                    {`${Job_id ? "Edit Notebook Job" : "Add Notebook Job"}`}
                  </Typography>
                  <ButtonUI variant="contained" color="success" disabled={disableSave} onClick={handleSave}>
                    Save
                  </ButtonUI>
                  <ButtonUI onClick={handleCancelClick} variant="contained" color="secondary" sx={{ ml: "15px" }}>
                    Cancel
                  </ButtonUI>

                  {Job_id ? (
                    <>
                      <ButtonUI
                        onClick={() => setOpenClone(true)}
                        variant="contained"
                        color="info"
                        sx={{ mx: "15px" }}
                        disabled={disableSave}
                      >
                        Clone
                      </ButtonUI>

                      <ButtonUI
                        variant="contained"
                        color="error"
                        onClick={() => {
                          setPrevState(setDeleteJobState, {
                            openConfirm: true,
                            confirmMessage: `Are you sure you want to delete "${jobState.jobName}" Job?"`,
                          });
                        }}
                        disabled={!Job_id}
                      >
                        Delete
                      </ButtonUI>
                    </>
                  ) : (
                    <></>
                  )}

                  <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                    <HelpContextIcon screen="Notebook Job" />
                  </Box>
                </Grid>

                <Grid className={classes.BigTileContainer} size={12}>
                  <FormTile>{addJobForm()}</FormTile>
                </Grid>
                <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                  <SmallToolbar>Schedule</SmallToolbar>
                  <FormTile
                    style={{
                      overflowX: "auto",
                    }}
                  >
                    <Scheduler
                      scheduleEnabled={jobState.scheduleEnabled}
                      cronValue={values.cronValue}
                      dispatchValues={dispatchValues}
                      toggleValue={toggleSchedule}
                      timeZone={jobState.timeZone}
                      nextRunTime={jobState.nextRunTime}
                    />
                  </FormTile>
                </Grid>
                <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                  <SmallToolbar>Next Job</SmallToolbar>
                  <FormTile>
                    <NextJobForm
                      dataPipelineList={dataPipelineList}
                      nextJobToggle={nextJobState.nextJobToggle}
                      nextDataPipelineId={nextJobState.nextDataPipelineId}
                      nextJobId={nextJobState.nextJobId}
                      errorDataPipelineId={nextJobState.errorDataPipelineId}
                      errorJobId={nextJobState.errorJobId}
                      setNextJobState={setNextJobState}
                    />
                  </FormTile>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>

      {openClone && (
        <CloneJob
          setOpenClone={setOpenClone}
          pipelineId={jobState.DataPipeline_id}
          handleSave={handleSave}
          JobType={JOB_BY_TYPE.notebook}
        />
      )}

      {deleteJobState.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: "Delete Notebook Job",
            popupMessage: deleteJobState.confirmMessage,
            handleCancel: () => handleCancel(setDeleteJobState, "openConfirm"),
            handleClick: deleteNotebookJob,
            loadingForConfirm: deleteJobState.loadingForConfirm,
          }}
        />
      )}
    </CommonLayout>
  );
}
